import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, CheckCircle2, MinusCircle, RefreshCw } from 'lucide-react';
import type { TwilioHealth } from '@/api/endpoints/platform';
import { Button } from '@/components/ui/button';
import { CopyLink } from '@/components/shared/CopyLink';
import { ErrorLine } from '@/components/backoffice/platform/PlatformBits';
import { healthSummary, twilioHealthChecks, type CheckStatus } from './twilioHealthChecks';

/**
 * Bilan de santé, at the top of TwilioConfig whatever the tab (W6, DUP-5:
 * the one diagnosis, TwilioDiagnostic redirects here). Each line: what,
 * cause, action. « Re-vérifier » reads Twilio again; nothing is written.
 */
const ICON: Record<CheckStatus, { Icon: typeof CheckCircle2; color: string }> = {
  ok: { Icon: CheckCircle2, color: 'var(--vf-fill-success)' },
  warn: { Icon: AlertTriangle, color: 'var(--vf-orange)' },
  error: { Icon: AlertCircle, color: 'var(--vf-fill-danger)' },
  skipped: { Icon: MinusCircle, color: 'var(--vf-text-3)' },
};

const TWIML_APPS_CONSOLE = 'https://console.twilio.com/us1/develop/voice/manage/twiml-apps';

export function TwilioHealthPanel({ health, isLoading, isFetching, isError, error, onRecheck }: {
  health?: TwilioHealth;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  onRecheck: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const checks = health ? twilioHealthChecks(health) : [];
  const { errors, warnings } = healthSummary(checks);
  return (
    <section aria-labelledby="twilio-health-title" aria-busy={isFetching} className="space-y-3 rounded-vf border p-4" style={{ borderColor: errors ? 'var(--vf-fill-danger)' : 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="twilio-health-title" className="text-base font-semibold">{t('admin:twilioConfig.health.title')}</h2>
          {health && (
            <p role="status" className="text-sm" style={{ color: errors ? 'var(--vf-fill-danger)' : 'var(--vf-text-2)' }}>
              {errors
                ? t('admin:twilioConfig.health.summaryErrors', { count: errors })
                : warnings
                  ? t('admin:twilioConfig.health.summaryWarnings', { count: warnings })
                  : t('admin:twilioConfig.health.summaryOk')}
            </p>
          )}
        </div>
        <Button type="button" variant="outline" onClick={onRecheck} disabled={isFetching} className="gap-2">
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden />
          {t('admin:twilioConfig.recheck')}
        </Button>
      </div>

      {isLoading && <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.health.checking')}</p>}
      {isError && <ErrorLine message={t('admin:twilioConfig.health.loadError')} error={error} onRetry={onRecheck} />}

      {checks.length > 0 && (
        <ul className="divide-y rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
          {checks.map((c) => {
            const { Icon, color } = ICON[c.status];
            return (
              <li key={c.id} className="flex gap-3 p-3" style={{ borderColor: 'var(--vf-border)' }} data-status={c.status}>
                <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} aria-hidden />
                <div className="min-w-0 flex-1 space-y-1 text-sm">
                  <p className="font-medium">
                    <span className="sr-only">{t(`admin:twilioConfig.health.status.${c.status}`)} : </span>
                    {t(`admin:twilioConfig.health.checks.${c.id}.title`)}
                  </p>
                  <p className="break-words" style={{ color: 'var(--vf-text-2)' }}>{t(`admin:twilioConfig.health.checks.${c.id}.${c.state}.cause`, c.params)}</p>
                  {c.status !== 'ok' && c.status !== 'skipped' && (
                    <p className="break-words">{t(`admin:twilioConfig.health.checks.${c.id}.${c.state}.action`, c.params)}</p>
                  )}
                  {c.copy && (
                    <div className="flex flex-wrap items-start gap-2 pt-1">
                      <CopyLink url={c.copy} label={t('admin:twilioConfig.health.copyVoiceUrl')} />
                      <a href={TWIML_APPS_CONSOLE} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center text-sm underline" style={{ color: 'var(--vf-accent-text)' }}>
                        {t('admin:twilioConfig.health.openTwimlApps')}
                      </a>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
